import React, { useState, useEffect, useRef } from "react";

const NAV_TABS = [
  { id: "home", icon: "🏠", label: "Home" },
  { id: "school", icon: "🏫", label: "School & Activities" },
  { id: "links", icon: "🔗", label: "Quick Links" },
  { id: "essential", icon: "🔒", label: "Essential Info" },
];

const TYPE_GRADIENTS = {
  General: "linear-gradient(135deg, #A89880 0%, #7A6A50 100%)",
  Summer: "linear-gradient(135deg, #E8B84B 0%, #C9963A 100%)",
};

function TypeFilterDropdown({ selectedTypes, onChange, allTypes }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const isFiltered = selectedTypes.length > 0;

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const toggle = (type) => onChange(selectedTypes.includes(type) ? selectedTypes.filter(t => t !== type) : [...selectedTypes, type]);

  return (
    <div ref={ref} style={{ position: "relative", display: "inline-block" }}>
      <button onClick={() => setOpen(o => !o)} style={{ display: "flex", alignItems: "center", gap: 6, padding: "9px 16px", borderRadius: 10, border: `1.5px solid ${isFiltered ? "var(--charcoal)" : "var(--border)"}`, background: isFiltered ? "var(--charcoal)" : "var(--white)", color: isFiltered ? "#fff" : "var(--charcoal)", fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
        Type
        {isFiltered && <span style={{ background: "#fff", color: "var(--charcoal)", borderRadius: 10, fontSize: 11, fontWeight: 700, padding: "1px 6px" }}>{selectedTypes.length}</span>}
      </button>
      {open && (
        <div style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: 50, background: "var(--white)", borderRadius: 12, border: "1px solid var(--border)", boxShadow: "0 4px 16px rgba(0,0,0,0.1)", minWidth: 160, overflow: "hidden" }}>
          {allTypes.map((type, i) => {
            const checked = selectedTypes.includes(type);
            return (
              <div key={type} onClick={() => toggle(type)} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", cursor: "pointer", borderBottom: i < allTypes.length - 1 ? "1px solid var(--border)" : "none", background: checked ? "var(--cream-hover)" : "transparent" }}>
                <span style={{ fontSize: 13, fontWeight: checked ? 600 : 400, color: "var(--charcoal)" }}>{type}</span>
                <div style={{ width: 18, height: 18, borderRadius: 5, flexShrink: 0, border: checked ? "none" : "2px solid var(--border)", background: checked ? "var(--sage)" : "transparent", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {checked && <span style={{ color: "white", fontSize: 11, fontWeight: 700 }}>✓</span>}
                </div>
              </div>
            );
          })}
          {selectedTypes.length > 0 && <div onClick={() => { onChange([]); setOpen(false); }} style={{ padding: "10px 16px", fontSize: 12, color: "var(--muted)", fontWeight: 600, cursor: "pointer", textAlign: "center", borderTop: "1px solid var(--border)" }}>Clear filter</div>}
        </div>
      )}
    </div>
  );
}

function CloseButton({ onClose }) {
  return (
    <button onClick={onClose} aria-label="Close" style={{ position: "absolute", top: 14, right: 14, width: 30, height: 30, borderRadius: "50%", background: "var(--cream-hover)", border: "none", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", zIndex: 2 }}>
      <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><path d="M1 1L12 12M12 1L1 12" stroke="#9E9B96" strokeWidth="1.8" strokeLinecap="round" /></svg>
    </button>
  );
}

function Sheet({ onClose, children }) {
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 200, display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
      <div onClick={onClose} style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.35)" }} />
      <div style={{ position: "relative", background: "var(--white)", borderRadius: "20px 20px 0 0", maxHeight: "88vh", overflowY: "auto", boxShadow: "0 -4px 24px rgba(0,0,0,0.12)" }}>
        <div style={{ display: "flex", justifyContent: "center", padding: "12px 0 0" }}><div style={{ width: 36, height: 4, borderRadius: 2, background: "var(--border)" }} /></div>
        <CloseButton onClose={onClose} />
        {children}
        <div style={{ height: 32 }} />
      </div>
    </div>
  );
}

function EditSheetShell({ title, onClose, onSave, saving, children }) {
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 300, display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
      <div onClick={onClose} style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.4)" }} />
      <div style={{ position: "relative", background: "var(--white)", borderRadius: "20px 20px 0 0", maxHeight: "88vh", display: "flex", flexDirection: "column", boxShadow: "0 -4px 24px rgba(0,0,0,0.12)" }}>
        <div style={{ display: "flex", justifyContent: "center", padding: "12px 0 4px" }}><div style={{ width: 36, height: 4, borderRadius: 2, background: "var(--border)" }} /></div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 20px 16px", borderBottom: "1px solid var(--border)" }}>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 14, fontWeight: 700, color: "var(--muted)", cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
          <span style={{ fontFamily: "'Playfair Display', serif", fontSize: 16, fontWeight: 600, color: "var(--charcoal)" }}>{title}</span>
          <button onClick={onSave} disabled={saving} style={{ background: "none", border: "none", fontSize: 14, fontWeight: 800, color: saving ? "var(--muted)" : "var(--sage)", cursor: saving ? "default" : "pointer", fontFamily: "inherit" }}>{saving ? "Saving…" : "Save"}</button>
        </div>
        <div style={{ overflowY: "auto", paddingBottom: 24 }}>{children}</div>
      </div>
    </div>
  );
}

const field = { padding: "12px 20px", borderBottom: "1px solid var(--border)" };
const label = { fontSize: 11, fontWeight: 800, letterSpacing: "0.07em", textTransform: "uppercase", color: "var(--muted)", marginBottom: 6 };
const textInputStyle = { width: "100%", border: "none", outline: "none", fontSize: 15, fontWeight: 500, color: "var(--charcoal)", fontFamily: "inherit", background: "transparent" };

function BucketEditSheet({ item, allTypes, onClose, onSaved }) {
  const [form, setForm] = useState({ name: item.name || "", type: item.type || [], who: item.who || "" });
  const [saving, setSaving] = useState(false);

  const toggleType = (t) => setForm(f => ({ ...f, type: f.type.includes(t) ? f.type.filter(x => x !== t) : [...f.type, t] }));

  const handleSave = async () => {
    setSaving(true);
    await fetch("/.netlify/functions/notion", {
      method: "PATCH", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ pageId: item.id, action: "updateBucketListItem", value: form }),
    });
    setSaving(false);
    onSaved({ ...item, ...form });
  };

  return (
    <EditSheetShell title="Edit Item" onClose={onClose} onSave={handleSave} saving={saving}>
      <div style={field}><div style={label}>Name</div><input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} style={textInputStyle} /></div>
      <div style={field}>
        <div style={label}>Type</div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 4 }}>
          {allTypes.map(t => (
            <button key={t} onClick={() => toggleType(t)} style={{ padding: "6px 12px", borderRadius: 20, border: `1.5px solid ${form.type.includes(t) ? "var(--sage)" : "var(--border)"}`, background: form.type.includes(t) ? "var(--sage)" : "transparent", color: form.type.includes(t) ? "#fff" : "var(--muted)", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>{t}</button>
          ))}
        </div>
      </div>
      <div style={{ ...field, borderBottom: "none" }}><div style={label}>Who's Going</div><input value={form.who} onChange={e => setForm({ ...form, who: e.target.value })} style={textInputStyle} /></div>
    </EditSheetShell>
  );
}

function BucketCard({ item, onTap, onToggle, readOnly = false }) {
  const primaryType = item.type?.[0];
  const gradient = TYPE_GRADIENTS[primaryType] || "linear-gradient(135deg, #A89880 0%, #7A6A50 100%)";

  return (
    <div style={{ background: "var(--white)", borderRadius: 16, marginBottom: 12, border: "1px solid var(--border)", overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.06)", opacity: item.done ? 0.65 : 1 }}>
      <div onClick={() => onTap(item)} style={{ height: 130, position: "relative", cursor: "pointer", background: item.coverPhoto ? `url(${item.coverPhoto}) center/cover no-repeat` : gradient }}>
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to bottom, rgba(0,0,0,0.0) 0%, rgba(0,0,0,0.5) 100%)" }} />
        <div style={{ position: "absolute", bottom: 10, left: 14, right: 14 }}>
          <div style={{ fontSize: 18, fontWeight: 800, color: "#fff", lineHeight: 1.2, textShadow: "0 1px 3px rgba(0,0,0,0.3)", textDecoration: item.done ? "line-through" : "none" }}>{item.name}</div>
        </div>
      </div>
      <div style={{ padding: "14px 16px 16px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div onClick={() => onTap(item)} style={{ display: "flex", gap: 6, flexWrap: "wrap", flex: 1, cursor: "pointer" }}>
            {item.type?.map(t => <span key={t} style={{ fontSize: 14, fontWeight: 600, padding: "5px 12px", borderRadius: 20, background: "var(--cream-hover)", color: "var(--charcoal)" }}>{t}</span>)}
          </div>
          {!readOnly && <div onClick={e => { e.stopPropagation(); onToggle(item.id, !item.done); }} style={{ width: 24, height: 24, borderRadius: 7, flexShrink: 0, border: item.done ? "none" : "2px solid var(--border)", background: item.done ? "var(--sage)" : "var(--white)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", transition: "all 0.15s", marginLeft: 12 }}>
            {item.done && <span style={{ color: "#fff", fontSize: 13, fontWeight: 700 }}>✓</span>}
          </div>}
        </div>
        {item.who && <div onClick={() => onTap(item)} style={{ fontSize: 15, color: "var(--muted)", marginTop: 8, cursor: "pointer" }}>👥 {item.who}</div>}
      </div>
    </div>
  );
}

function ItemDetail({ item: initialItem, allTypes, onClose, onToggle, readOnly = false }) {
  const [item, setItem] = useState(initialItem);
  const [showEdit, setShowEdit] = useState(false);
  const primaryType = item.type?.[0];
  const gradient = TYPE_GRADIENTS[primaryType] || "linear-gradient(135deg, #A89880 0%, #7A6A50 100%)";
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`/.netlify/functions/notion?type=blocks&pageId=${item.id}`)
      .then(r => r.json()).then(d => setBlocks(d.blocks || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [item.id]);

  return (
    <>
      <Sheet onClose={onClose}>
        <div style={{ height: 160, position: "relative", margin: "8px 0 0", background: item.coverPhoto ? `url(${item.coverPhoto}) center/cover no-repeat` : gradient }}>
          <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to bottom, rgba(0,0,0,0.0) 0%, rgba(0,0,0,0.55) 100%)" }} />
          <div style={{ position: "absolute", bottom: 14, left: 20, right: 20 }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: "#fff", lineHeight: 1.2, textShadow: "0 1px 4px rgba(0,0,0,0.3)", marginBottom: 6 }}>{item.name}</div>
            <div style={{ display: "flex", gap: 6 }}>
              {item.type?.map(t => <span key={t} style={{ fontSize: 11, fontWeight: 600, padding: "3px 8px", borderRadius: 20, background: "rgba(255,255,255,0.25)", color: "#fff", backdropFilter: "blur(4px)" }}>{t}</span>)}
            </div>
          </div>
        </div>
        {!readOnly && (
          <div style={{ padding: "14px 20px 16px", display: "flex", gap: 8 }}>
            <button onClick={() => { onToggle(item.id, !item.done); onClose(); }} style={{ flex: 1, padding: "11px 0", borderRadius: 10, border: "none", cursor: "pointer", background: item.done ? "var(--cream-hover)" : "var(--sage)", color: item.done ? "var(--muted)" : "#fff", fontWeight: 700, fontSize: 13, fontFamily: "inherit" }}>
              {item.done ? "✓ Completed — Mark Undone" : "Mark as Done"}
            </button>
            <button onClick={() => setShowEdit(true)} style={{ padding: "11px 14px", borderRadius: 10, border: "1.5px solid var(--sage)", color: "var(--sage)", fontWeight: 700, fontSize: 13, background: "var(--white)", cursor: "pointer", fontFamily: "inherit" }}>Edit</button>
            <a href={item.notionUrl} target="_blank" rel="noreferrer" style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "11px 14px", borderRadius: 10, border: "1.5px solid var(--border)", color: "var(--muted)", fontWeight: 600, fontSize: 13, textDecoration: "none", background: "var(--cream-hover)" }}>Notion ↗</a>
          </div>
        )}
        {item.who && (
          <div style={{ margin: "0 20px 12px", display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 20 }}>👥</span>
            <span style={{ fontSize: 14, color: "var(--charcoal)", fontWeight: 500 }}>{item.who}</span>
          </div>
        )}
        {loading && (
          <div style={{ margin: "0 20px 16px", padding: "12px 14px", background: "var(--cream-hover)", borderRadius: 10, fontSize: 13, color: "var(--muted)", fontStyle: "italic" }}>Loading…</div>
        )}
        {!loading && blocks.length > 0 && (
          <div style={{ margin: "0 20px 16px", padding: "12px 14px", background: "var(--cream-hover)", borderRadius: 10, fontSize: 13, color: "var(--charcoal)", lineHeight: 1.6 }}>
            {blocks.map((b, i) => (
              <div key={i} style={{ marginBottom: i < blocks.length - 1 ? 6 : 0, paddingLeft: b.type === "bulleted_list_item" ? 14 : 0, position: "relative" }}>
                {b.type === "bulleted_list_item" && <span style={{ position: "absolute", left: 0, color: "var(--sage)", fontWeight: 800 }}>•</span>}
                {b.text}
              </div>
            ))}
          </div>
        )}
        {!loading && blocks.length === 0 && (
          <div style={{ margin: "0 20px 16px", padding: "12px 14px", background: "var(--cream-hover)", borderRadius: 10, fontSize: 13, color: "var(--muted)", fontStyle: "italic", textAlign: "center" }}>
            No notes yet. <a href={item.notionUrl} target="_blank" rel="noreferrer" style={{ color: "var(--sage)", textDecoration: "none", fontWeight: 700 }}>Add in Notion ↗</a>
          </div>
        )}
      </Sheet>
      {showEdit && <BucketEditSheet item={item} allTypes={allTypes} onClose={() => setShowEdit(false)} onSaved={(updated) => { setItem(updated); setShowEdit(false); }} />}
    </>
  );
}

export default function BucketListPage({ onBack, onTabTap, readOnly = false }) {
  const [tab, setTab] = useState("todo");
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [allTypes, setAllTypes] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);

  useEffect(() => {
    fetch("/.netlify/functions/notion?type=bucketListTypes")
      .then(r => r.json()).then(d => setAllTypes(d.options || [])).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    fetch(`/.netlify/functions/notion?type=bucketList&done=${tab === "done"}`)
      .then(r => r.json()).then(d => setItems(d.items || []))
      .finally(() => setLoading(false));
  }, [tab]);

  const handleToggle = async (id, val) => {
    setItems(prev => prev.map(i => i.id === id ? { ...i, done: val } : i));
    if (selectedItem?.id === id) setSelectedItem(prev => ({ ...prev, done: val }));
    await fetch("/.netlify/functions/notion", {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ pageId: id, action: "updateBucketListItem", value: { done: val } }),
    });
  };

  const filtered = items.filter(i => selectedTypes.length === 0 || i.type?.some(t => selectedTypes.includes(t)));

  return (
    <>
      <div className="app">
        <header style={{ background: "var(--white)", padding: "54px 20px 0", borderBottom: "2px solid var(--border)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
            <button onClick={onBack} style={{ width: 34, height: 34, borderRadius: "50%", background: "var(--cream-hover)", border: "none", fontSize: 18, color: "var(--mid)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "inherit" }}>‹</button>
            <span style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, fontWeight: 600, color: "var(--charcoal)" }}>🪣 Bucket List</span>
          </div>
          <div style={{ display: "flex" }}>
            {[{ id: "todo", label: "To Do" }, { id: "done", label: "Done" }].map(t => (
              <button key={t.id} onClick={() => setTab(t.id)} style={{ flex: 1, textAlign: "center", padding: "10px 0", fontSize: 13, fontWeight: 700, cursor: "pointer", background: "none", border: "none", fontFamily: "inherit", borderBottom: `2.5px solid ${tab === t.id ? "var(--sage)" : "transparent"}`, color: tab === t.id ? "var(--sage)" : "var(--muted)" }}>{t.label}</button>
            ))}
          </div>
        </header>

        <div className="scroll-body" style={{ padding: "12px 16px" }}>
          <div style={{ marginBottom: 12 }}>
            <TypeFilterDropdown selectedTypes={selectedTypes} onChange={setSelectedTypes} allTypes={allTypes} />
          </div>
          {loading ? (
            <div style={{ padding: 40, textAlign: "center", color: "var(--muted)" }}>Loading…</div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: "center", padding: "48px 0", color: "var(--muted)", fontSize: 14 }}>{tab === "done" ? "Nothing completed yet." : "Nothing here yet."}</div>
          ) : (
            filtered.map(item => <BucketCard key={item.id} item={item} onTap={setSelectedItem} onToggle={handleToggle} readOnly={readOnly} />)
          )}
        </div>

        <nav style={{ position: "fixed", bottom: 0, left: "50%", transform: "translateX(-50%)", width: "100%", maxWidth: "var(--app-max-width)", background: "var(--white)", borderTop: "1px solid var(--border)", display: "flex", padding: "10px 0 20px", zIndex: 100 }}>
          {NAV_TABS.map(t => (
            <div key={t.id} onClick={() => onTabTap?.(t.id)} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 3, cursor: "pointer", padding: "4px 0" }}>
              <div style={{ fontSize: 22 }}>{t.icon}</div>
              <div style={{ fontSize: 10, fontWeight: 700, color: "var(--muted)", textAlign: "center", lineHeight: 1.2 }}>{t.label}</div>
            </div>
          ))}
        </nav>
      </div>

      {selectedItem && (
        <ItemDetail
          item={selectedItem}
          allTypes={allTypes}
          onClose={() => setSelectedItem(null)}
          onToggle={handleToggle}
          readOnly={readOnly}
        />
      )}
    </>
  );
}
